import { FC, useEffect, useRef } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { VirtualizedAutocomplete } from 'src/components/virtualized_autocomplete';
import { orderingProviderKeys } from 'src/constants/orders';
import { useIsOrderingPhysician } from 'src/hooks/use_auth';
import { OrderingProviderFieldsFragment } from 'src/types';

interface OrderingProviderAutocompleteProps {
  orderRepository: LdtOrderRepository;
  value?: OrderingProviderFieldsFragment | null; // Selected provider
  onChange: (provider: OrderingProviderFieldsFragment | null) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  onEdit?: (provider: OrderingProviderFieldsFragment) => void;
}

export const OrderingProviderAutocomplete: FC<
  OrderingProviderAutocompleteProps
> = ({
  orderRepository,
  value,
  onChange,
  onBlur,
  error = false,
  helperText,
  required = false,
  disabled = false,
  label = 'Ordering Provider',
  onEdit,
}) => {
  const theme = useTheme();
  const isOrderingPhysicianUser = useIsOrderingPhysician();
  const hasAutoSelectedRef = useRef(false);

  // GraphQL only accepts spaces and alphanumeric in search;
  // strip other characters to avoid request failures.
  const sanitizeSearchText = (text?: string): string | undefined => {
    if (!text) return undefined;
    const sanitized = text.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    return sanitized === '' ? undefined : sanitized;
  };

  // Format provider display name
  const getProviderLabel = (
    provider: OrderingProviderFieldsFragment | null,
  ): string => {
    if (!provider) return '';
    const { orderingPhysician } = provider;
    if (!orderingPhysician) return '';

    const { firstName, lastName } = orderingPhysician;
    const physicianName = [firstName, lastName].filter(Boolean).join(' ');

    return provider.siteName != null
      ? `${physicianName} [${provider.siteName}]`
      : physicianName;
  };

  // Adapter to convert cursor-based response to CursorPaginatedResponse
  const fetchProviders = async (params: {
    searchText?: string;
    cursor?: string | null;
    first: number;
  }) => {
    const sanitizedSearchText = sanitizeSearchText(params.searchText);
    const result = await orderRepository.getOrderingProviders({
      ...params,
      searchText: sanitizedSearchText,
    });
    const items = result.items;

    return {
      items: items.filter(
        (item): item is OrderingProviderFieldsFragment => item !== null,
      ),
      nextCursor: result.nextCursor,
      hasNextPage: result.hasNextPage,
    };
  };

  const {
    data: orderingProvidersForUser,
    isLoading: isLoadingOrderingProviders,
  } = useQuery({
    queryKey: orderingProviderKeys.all,
    queryFn: () =>
      orderRepository.getOrderingProviders({
        cursor: null,
        first: 1,
      }),
    enabled: isOrderingPhysicianUser,
  });

  useEffect(() => {
    // For ordering physician users, if there's at least one provider and no value is set,
    // auto-select the first one (backend should only return their provider, but we handle
    // edge cases where there might be multiple due to bad data)
    // Only check on first page (no cursor) with no search text to avoid auto-selecting during search
    if (
      isOrderingPhysicianUser &&
      !isLoadingOrderingProviders &&
      orderingProvidersForUser?.items &&
      orderingProvidersForUser.items.length > 0 &&
      !value &&
      !hasAutoSelectedRef.current
    ) {
      hasAutoSelectedRef.current = true;
      onChange(orderingProvidersForUser.items[0]);
    }
  }, [
    isLoadingOrderingProviders,
    isOrderingPhysicianUser,
    onChange,
    orderingProvidersForUser,
    value,
  ]);

  // Reset auto-select flag when value changes externally
  useEffect(() => {
    if (value) {
      hasAutoSelectedRef.current = true;
    }
  }, [value]);

  // Custom render option with edit button on hover
  const renderOption = (provider: OrderingProviderFieldsFragment) => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',

          '&:hover .edit-button': {
            opacity: 1,
            visibility: 'visible',
          },
        }}
      >
        <Typography
          variant='body2'
          noWrap
          title={getProviderLabel(provider)}
          sx={{ flex: 1 }}
        >
          {getProviderLabel(provider)}
        </Typography>
        {onEdit && (
          <Button
            className='edit-button'
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onEdit(provider);
            }}
            sx={{
              flexShrink: 0,
              opacity: 0,
              visibility: 'hidden',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.dark
                    : theme.palette.primary.light,
              },
            }}
          >
            EDIT
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box data-testid='ordering-provider-autocomplete'>
      <VirtualizedAutocomplete<OrderingProviderFieldsFragment>
        queryKey={orderingProviderKeys.all}
        fetchPage={fetchProviders}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        getItemId={(provider) => provider.id}
        getItemLabel={getProviderLabel}
        renderOption={renderOption}
        label={label}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        placeholder='Type to search providers'
      />
    </Box>
  );
};
